import { HttpErrorResponse } from '@angular/common/http';

import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';
import {
    IAnnualReport,
    IAnnualReportPdfRequest,
    IFinStatements,
    IFinancialStatementSource,
    IPrintOptions,
    ISetupData,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { Action, createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import { ReportType } from '../../../../aumico-lib-core-fe/src/lib';
import { AnnualReportMetaInformationDTO } from '../dtos/annualReportMetaInformation.dto';
import { BulkCreateFinancialYearJobStatusDto } from '../dtos/bulk-create-financial-year-job-status.dto';
import { BulkCreateFinancialYearPreviewDto } from '../dtos/bulk-create-financial-year-preview.dto';
import { BulkCreateFinancialYearRequestDto } from '../dtos/bulk-create-financial-year-request.dto';
import { BulkCreateFinancialYearResponseDto } from '../dtos/bulk-create-financial-year-response.dto';
import { CreateAnnualReportResponseDto } from '../dtos/create-annual-report-response.dto';
import { IDispatchAnnualReportsFilter } from '../dtos/dispatch-annual-reports-filter.dto';
import { IAuditLogDTO } from '../dtos/get-audit-log.dto';
import { PrintUsingPdfTronDto } from '../dtos/print-using-pdf-tron.dto';
import { AnnualReportExcelSheet } from '../interfaces/export-excel/annual-report-excel-sheet.interface';

export const createAnnualReport = createAction(
    '[Aumico-AnnualReport] createAnnualReport',
    props<{
        payload: { setupData: ISetupData; existingAnnualReportEncryptedId?: string; existingProjectId?: string };
    }>(),
);
export const onCreateAnnualReportSuccessfully = createAction(
    '[Aumico-AnnualReport] onCreateAnnualReportSuccessfully',
    props<{ payload: CreateAnnualReportResponseDto }>(),
);
export const onCreateAnnualReportFailed = createAction(
    '[Aumico-AnnualReport] onCreateAnnualReportFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const updateAnnualReport = createAction(
    '[Aumico-AnnualReport] updateAnnualReport',
    props<{ payload: { annualReport: IAnnualReport; annualReportEncryptedId: string } }>(),
);
export const enforceStateUpdateForAnnualReport = createAction(
    '[Aumico-AnnualReport] enforceStateUpdateForAnnualReport',
    props<{ payload: { annualReport: IAnnualReport } }>(),
);
export const updateFinStatementsOfAnnualReport = createAction(
    '[Aumico-AnnualReport] updateFinStatementsOfAnnualReport',
    props<{ payload: { finStatements: IFinStatements[]; annualReportEncryptedId: string } }>(),
);

export const onUpdateAnnualReportSuccessfully = createAction(
    '[Aumico-AnnualReport] onUpdateAnnualReportSuccessfully',
    props<{ payload: { annualReport: IAnnualReport } }>(),
);
export const onUpdateAnnualReportFailed = createAction(
    '[Aumico-AnnualReport] onUpdateAnnualReportFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// Scoped print-options save: persists ONLY printOptions via the /print-options PATCH endpoint,
// which stays available on a sealed report (sealing locks financial content, not print layout).
export const updatePrintOptions = createAction(
    '[Aumico-AnnualReport] updatePrintOptions',
    props<{ payload: { annualReportEncryptedId: string; printOptions: IPrintOptions } }>(),
);
export const onUpdatePrintOptionsSuccessfully = createAction(
    '[Aumico-AnnualReport] onUpdatePrintOptionsSuccessfully',
    props<{ payload: { printOptions: IPrintOptions } }>(),
);
export const onUpdatePrintOptionsFailed = createAction(
    '[Aumico-AnnualReport] onUpdatePrintOptionsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getActiveAnnualReports = createAction(
    '[Aumico-AnnualReport] getActiveAnnualReports',
    props<{ payload: { filter?: IDispatchAnnualReportsFilter } }>(),
);
export const onGettingActiveAnnualReportsSuccessfully = createAction(
    '[Aumico-AnnualReport] onGettingActiveAnnualReportsSuccessfully',
    props<{ payload: AnnualReportMetaInformationDTO[] }>(),
);
export const onGettingActiveAnnualReportsFailed = createAction(
    '[Aumico-AnnualReport] onGettingActiveAnnualReportsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getArchivedAnnualReports = createAction(
    '[Aumico-AnnualReport] getArchivedAnnualReports',
    props<{ payload: { filter?: IDispatchAnnualReportsFilter } }>(),
);
export const onGettingArchivedAnnualReportsSuccessfully = createAction(
    '[Aumico-AnnualReport] onGettingArchivedAnnualReportsSuccessfully',
    props<{ payload: AnnualReportMetaInformationDTO[] }>(),
);
export const onGettingArchivedAnnualReportsFailed = createAction(
    '[Aumico-AnnualReport] onGettingArchivedAnnualReportsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getActiveAnnualReport = createAction(
    '[Aumico-AnnualReport] getActiveAnnualReport',
    props<{ payload: string }>(),
);
export const onGetActiveAnnualReportSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetActiveAnnualReportSuccessfully',
    props<{ payload: IAnnualReport }>(),
);
export const onGetActiveAnnualReportFailed = createAction(
    '[Aumico-AnnualReport] onGetActiveAnnualReportFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getAnnualReportsListToCopyFrom = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Get Annual Reports List To Copy From': emptyProps(),
        'Get Annual Reports List To Copy From Success': props<{ payload: AnnualReportMetaInformationDTO[] }>(),
        'Get Annual Reports List To Copy From Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const getAnnualReportForMappingAutoFill = createAction(
    '[Aumico-AnnualReport] getAnnualReportForMappingAutoFill',
    props<{ payload: string }>(),
);
export const onGetAnnualReportForMappingAutoFillSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportForMappingAutoFillSuccessfully',
    props<{ payload: IAnnualReport }>(),
);
export const onGetAnnualReportForMappingAutoFillFailed = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportForMappingAutoFillFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const setBalanceAutoFillIdWithChosenAnnualReportId = createAction(
    '[Aumico-AnnualReport] setBalanceAutoFillIdWithChosenAnnualReportId',
    props<{ payload: string }>(),
);

export const setIncomeAutoFillIdWithChosenAnnualReportId = createAction(
    '[Aumico-AnnualReport] setIncomeAutoFillIdWithChosenAnnualReportId',
    props<{ payload: string }>(),
);

export const setActiveAnnualReportEncryptedId = createAction(
    '[Aumico-AnnualReport] setActiveAnnualReportEncryptedId',
    props<{ payload: string }>(),
);

export const resetBalanceAutoFillId = createAction('[Aumico-AnnualReport] resetBalanceAutoFillId');

export const resetIncomeAutoFillId = createAction('[Aumico-AnnualReport] resetIncomeAutoFillId');

export const resetFcp = createAction('[Aumico-AnnualReport] resetFcp');

export const resetActiveAnnualReport = createAction('[Aumico-AnnualReport] resetActiveAnnualReport');

export const resetAnnualReportForBalanceAutoFill = createAction(
    '[Aumico-AnnualReport] resetAnnualReportForBalanceAutoFill',
);

export const resetAnnualReportForIncomeAutoFill = createAction(
    '[Aumico-AnnualReport] resetAnnualReportForIncomeAutoFill',
);

export const removeActiveAnnualReport = createAction('[Aumico-AnnualReport] removeActiveAnnualReport');

// Action to remove all active editing before start a new editing
export const flush = createAction('[Aumico-AnnualReport] flush');

export const getPreviousAnnualReportOptions = createAction(
    '[Aumico-AnnualReport] getPreviousAnnualReportOptions',
    props<{
        companyId: string;
        currentAnnualReportId: string;
        legalFormId: string;
        accountingBasisId?: string;
        locale?: string;
    }>(),
);

export const onGetPreviousAnnualReportOptionsSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetPreviousAnnualReportOptionsSuccessfully',
    props<{ payload: Partial<IAnnualReport>[] }>(),
);

export const onGetPreviousAnnualReportOptionsFailed = createAction(
    '[Aumico-AnnualReport] onGetPreviousAnnualReportOptionsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getAnnualReportsByCompanyId = createAction(
    '[Aumico-AnnualReport] getAnnualReportsByCompanyId',
    props<{
        payload: string;
    }>(),
);

export const onGetAnnualReportsByCompanyIdSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportsByCompanyIdSuccessfully',
    props<{ payload: IAnnualReport[] }>(),
);

export const onGetAnnualReportsByCompanyIdFailed = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportsByCompanyIdFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getBalanceAutofill = createAction(
    '[Aumico-AnnualReport] getBalanceAutofill',
    props<{ payload: string }>(),
);

export const onGetBalanceAutofillSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetBalanceAutofillSuccessfully',
    props<{ payload: IAnnualReport }>(),
);

export const onGetBalanceAutofillFailed = createAction(
    '[Aumico-AnnualReport] onGetBalanceAutofillFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getIncomeAutofill = createAction('[Aumico-AnnualReport] getIncomeAutofill', props<{ payload: string }>());

export const onGetIncomeAutofillSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetIncomeAutofillSuccessfully',
    props<{ payload: IAnnualReport }>(),
);

export const onGetIncomeAutofillFailed = createAction(
    '[Aumico-AnnualReport] onGetIncomeAutofillFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const setActiveAnnualReport = createAction(
    '[Aumico-AnnualReport] setActiveAnnualReport',
    props<{ payload: IAnnualReport }>(),
);

export const setBalanceFinancialStatementSource = createAction(
    '[Aumico-AnnualReport] setBalanceFinancialStatementSource',
    props<{ payload: IFinancialStatementSource }>(),
);

export const setIncomeFinancialStatementSource = createAction(
    '[Aumico-AnnualReport] setIncomeFinancialStatementSource',
    props<{ payload: IFinancialStatementSource }>(),
);

export const onGetAnnualReportByCompanyIdFailed = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportByCompanyIdFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const triggerFinancialStatementImportLoseFocus = createAction(
    '[Aumico-AnnualReport] triggerFinancialStatementImportLoseFocus',
);

export const printAnnualReport = createAction(
    '[Aumico-AnnualReport] printAnnualReport',
    props<{ payload: IAnnualReportPdfRequest }>(),
);
export const onPrintAnnualReportSuccessfully = createAction(
    '[Aumico-AnnualReport] onPrintAnnualReportSuccessfully',
    props<{ payload: any }>(),
);
export const onPrintAnnualReportFailed = createAction(
    '[Aumico-AnnualReport] onPrintAnnualReportFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const printAnnualReportUsingPdfTron = createActionGroup({
    source: '[Aumico-AnnualReport] Print Annual Report Using Pdf Tron',
    events: {
        'Print Annual Report Using Pdf Tron': props<{
            payload: PrintUsingPdfTronDto;
        }>(),
        'Print Annual Report Using Pdf Tron Success': props<{ payload: any }>(),
        'Print Annual Report Using Pdf Tron Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const printAnnualReportUsingPdfTronV2 = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Print Annual Report Using Pdf Tron V2': props<{ payload: PrintUsingPdfTronDto }>(),
        'Print Annual Report Using Pdf Tron V2 Success': props<{ payload: { pdfUrl: string } }>(),
        'Print Annual Report Using Pdf Tron V2 Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportAnnualReportToWord = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Export Annual Report To Word': props<{
            payload: PrintUsingPdfTronDto;
        }>(),
        'Export Annual Report To Word Success': emptyProps(),
        'Export Annual Report To Word Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportAnnualReportToWordV2 = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Export Annual Report To Word V2': props<{
            payload: PrintUsingPdfTronDto;
        }>(),
        'Export Annual Report To Word V2 Success': emptyProps(),
        'Export Annual Report To Word V2 Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportAnnualReportExcel = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Export Annual Report Excel': props<{ payload: { sheets: AnnualReportExcelSheet[] } }>(),
        'Export Annual Report Excel Success': emptyProps(),
        'Export Annual Report Excel Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportAnnualReportExcelV2 = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Export Annual Report Excel V2': props<{ payload: { sheets: AnnualReportExcelSheet[] } }>(),
        'Export Annual Report Excel V2 Success': emptyProps(),
        'Export Annual Report Excel V2 Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportAnnualReportToExcelStructured = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Export Annual Report To Excel Structured': props<{
            payload: PrintUsingPdfTronDto;
        }>(),
        'Export Annual Report To Excel Structured Success': emptyProps(),
        'Export Annual Report To Excel Structured Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const exportAnnualReportToExcelStructuredV2 = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Export Annual Report To Excel Structured V2': props<{
            payload: PrintUsingPdfTronDto;
        }>(),
        'Export Annual Report To Excel Structured V2 Success': emptyProps(),
        'Export Annual Report To Excel Structured V2 Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export const archiveAnnualReports = createAction(
    '[Aumico-AnnualReport] archiveAnnualReports',
    props<{ annualReportIds: string[] }>(),
);

export const onArchiveAnnualReportsSuccessfully = createAction(
    '[Aumico-AnnualReport] onArchiveAnnualReportsSuccessfully',
    props<{ annualReportIds: string[] }>(),
);

export const onArchiveAnnualReportsFailed = createAction(
    '[Aumico-AnnualReport] onArchiveAnnualReportsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const restoreAnnualReports = createAction(
    '[Aumico-AnnualReport] restoreAnnualReports',
    props<{ annualReportIds: string[] }>(),
);
export const onRestoreAnnualReportsSuccessfully = createAction(
    '[Aumico-AnnualReport] onRestoreAnnualReportsSuccessfully',
    props<{ annualReportIds: string[] }>(),
);
export const onRestoreAnnualReportsFailed = createAction(
    '[Aumico-AnnualReport] onRestoreAnnualReportsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const deleteAnnualReports = createAction(
    '[Aumico-AnnualReport] deleteAnnualReports',
    props<{ annualReportIds: string[] }>(),
);
export const onDeleteAnnualReportsSuccessfully = createAction(
    '[Aumico-AnnualReport] onDeleteAnnualReportsSuccessfully',
    props<{ annualReportIds: string[] }>(),
);
export const onDeleteAnnualReportsFailed = createAction(
    '[Aumico-AnnualReport] onDeleteAnnualReportsFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const setPrintView = createAction(
    '[Aumico-AnnualReport] setPrintView',
    props<{ payload: PrintUsingPdfTronDto }>(),
);

export const updateAnnualReportPlaceHolder = createAction(
    '[Aumico-AnnualReport] updateAnnualReportPlaceHolder',
    props<{ payload: IDynamicKeyValue }>(),
);

export const updateUserPlaceholderData = createAction(
    '[Aumico-AnnualReport] updateUserPlaceholderData',
    props<{ userName: string }>(),
);
export const updateOrganizationPlaceholderData = createAction(
    '[Aumico-AnnualReport] organizationPlaceholder',
    props<{ organization: GetOrganizationDto }>(),
);
export const updateAopSnapshotPlaceholder = createAction(
    '[Aumico-AnnualReport] updateAopSnapshotPlaceholder',
    props<{ aopSnapshot: string }>(),
);

export const updateAopSnapshotCurrentYearPlaceholder = createAction(
    '[Aumico-AnnualReport] updateAopSnapshotCurrentYearPlaceholder',
    props<{ aopSnapshotCurrentYear: string }>(),
);

export const resetAnnualReports = createAction('[Aumico-AnnualReport] resetAnnualReports');

export const getAnnualReportToCopyNotesStructureFrom = createAction(
    '[Aumico-AnnualReport] getAnnualReportToCopyNotesStructureFrom',
    props<{ payload: string }>(),
);
export const onGetAnnualReportToCopyNotesStructureFromSuccessfully = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportToCopyNotesStructureFromSuccessfully',
    props<{ payload: IAnnualReport }>(),
);
export const onGetAnnualReportToCopyNotesStructureFromFailed = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportToCopyNotesStructureFromFailed',
    props<{ payload: HttpErrorResponse }>(),
);
export const updateFcpState = createAction('[Annual Report] Update FCP State', props<{ payload: boolean }>());

// Seal Annual Report Actions
export const sealAnnualReport = createAction('[Aumico-AnnualReport] sealAnnualReport', props<{ payload: string }>());
export const onSealAnnualReportSuccessfully = createAction('[Aumico-AnnualReport] onSealAnnualReportSuccessfully');
export const onSealAnnualReportFailed = createAction(
    '[Aumico-AnnualReport] onSealAnnualReportFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// Unseal Annual Report Actions
export const unsealAnnualReport = createAction(
    '[Aumico-AnnualReport] unsealAnnualReport',
    props<{ payload: string }>(),
);
export const onUnsealAnnualReportSuccessfully = createAction('[Aumico-AnnualReport] onUnsealAnnualReportSuccessfully');
export const onUnsealAnnualReportFailed = createAction(
    '[Aumico-AnnualReport] onUnsealAnnualReportFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// Audit Logs Actions
export const getAnnualReportSealHistory = createAction(
    '[Aumico-AnnualReport] getAnnualReportSealHistory',
    props<{ payload: string }>(), // payload is annualReportId
);
export const onGetAnnualReportSealHistorySuccessfully = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportSealHistorySuccessfully',
    props<{ payload: IAuditLogDTO[] }>(),
);
export const onGetAnnualReportSealHistoryFailed = createAction(
    '[Aumico-AnnualReport] onGetAnnualReportSealHistoryFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const getNextBalanceSheetDateForCompanyById = createActionGroup({
    source: '[Aumico-AnnualReport]',
    events: {
        'Get Next Balance Sheet Date For Company By Id': props<{
            payload: { companyId: string; reportType: ReportType };
        }>(),
        'Get Next Balance Sheet Date For Company By Id Success': props<{ payload: Date }>(),
        'Get Next Balance Sheet Date For Company By Id Failed': props<{ payload: HttpErrorResponse }>(),
    },
});

export interface ActionWithPayload<T> extends Action {
    payload: T;
}

export const setAnnualReportMetaInfo = createAction(
    '[Annual Report] Set Annual Report Meta Info',
    props<{ annualReportMetaInfo: AnnualReportMetaInformationDTO; annualReport: IAnnualReport }>(),
);

// Bulk create financial year actions
export const bulkCreateFinancialYearPreview = createAction(
    '[Aumico-AnnualReport] bulkCreateFinancialYearPreview',
    props<{ payload: BulkCreateFinancialYearRequestDto }>(),
);

export const onBulkCreateFinancialYearPreviewSuccessfully = createAction(
    '[Aumico-AnnualReport] onBulkCreateFinancialYearPreviewSuccessfully',
    props<{ payload: BulkCreateFinancialYearPreviewDto }>(),
);

export const onBulkCreateFinancialYearPreviewFailed = createAction(
    '[Aumico-AnnualReport] onBulkCreateFinancialYearPreviewFailed',
    props<{ payload: HttpErrorResponse }>(),
);

export const bulkCreateFinancialYear = createAction(
    '[Aumico-AnnualReport] bulkCreateFinancialYear',
    props<{ payload: BulkCreateFinancialYearRequestDto }>(),
);

export const onBulkCreateFinancialYearSuccessfully = createAction(
    '[Aumico-AnnualReport] onBulkCreateFinancialYearSuccessfully',
    props<{ payload: BulkCreateFinancialYearResponseDto }>(),
);

export const onBulkCreateFinancialYearFailed = createAction(
    '[Aumico-AnnualReport] onBulkCreateFinancialYearFailed',
    props<{ payload: HttpErrorResponse }>(),
);

// Actions for polling job status
export const startPollingBulkCreateStatus = createAction(
    '[Aumico-AnnualReport] startPollingBulkCreateStatus',
    props<{ jobId: string }>(),
);

export const onBulkCreateStatusUpdate = createAction(
    '[Aumico-AnnualReport] onBulkCreateStatusUpdate',
    props<{ payload: BulkCreateFinancialYearJobStatusDto }>(),
);

export const onBulkCreateJobCompleted = createAction(
    '[Aumico-AnnualReport] onBulkCreateJobCompleted',
    props<{ payload: BulkCreateFinancialYearJobStatusDto }>(),
);

export const onBulkCreateJobFailed = createAction(
    '[Aumico-AnnualReport] onBulkCreateJobFailed',
    props<{ payload: BulkCreateFinancialYearJobStatusDto }>(),
);

export const stopPollingBulkCreateStatus = createAction(
    '[Aumico-AnnualReport] stopPollingBulkCreateStatus',
);

export const patchActiveAnnualReportIntegrations = createAction(
    '[Aumico-AnnualReport] patchActiveAnnualReportIntegrations',
    props<{ integrations: IAnnualReport['integrations'] }>()
);
